import React from 'react'
import styled from 'styled-components'
import { Button } from 'sutra/components/Button'

import { lightText } from 'assets/styles/variables'
import { IconCards } from 'components/empty-state/IconCards'

export interface EmptyStateAction {
  label: string
  onClick: () => void
  disabled?: boolean
  variant?: 'primary' | 'secondary'
}

type Props = {
  icon?: React.ReactNode
  title?: string
  description?: React.ReactNode
  actions?: EmptyStateAction[]
  fullHeight?: 'container' | 'viewport'
  /** Width of the text column. Use '300px' for the standard centered layout,
   *  or '100%' to fill a narrow container (e.g. a chart legend panel). */
  textWidth: string
  dataTestId?: string
}

export const EmptyState: React.FC<Props> = ({
  icon,
  title,
  description,
  actions,
  fullHeight,
  textWidth,
  dataTestId,
}) => {
  return (
    <OuterWrapper $fullHeight={fullHeight ?? 'none'} data-testid={dataTestId}>
      <ContentWrapper>
        {icon && <IconCards icon={icon} />}

        <TextContent $textWidth={textWidth}>
          <Title>{title}</Title>
          <Description>{description}</Description>
          {actions && actions.length > 0 && (
            <ActionsWrapper>
              {actions.map((action, index) => (
                <Button
                  key={index}
                  type="button"
                  $variant={action.variant ?? 'primary'}
                  $size="sm"
                  onClick={action.onClick}
                  disabled={action.disabled}
                >
                  {action.label}
                </Button>
              ))}
            </ActionsWrapper>
          )}
        </TextContent>
      </ContentWrapper>
    </OuterWrapper>
  )
}

const OuterWrapper = styled.div<{
  $fullHeight: 'container' | 'viewport' | 'none'
}>`
  width: 100%;
  height: 100%;
  min-height: ${(props) => {
    if (props.$fullHeight === 'viewport') return '100vh'
    if (props.$fullHeight === 'container') return '100%'
    return 'auto'
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  box-sizing: border-box;
  background: white;
  container-type: inline-size;
`

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 32px;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: center;
  width: 100%;
  min-width: 0;

  /* When container is narrow, stack vertically with left alignment */
  @container (max-width: 550px) {
    flex-direction: column;
    align-items: flex-start;
  }
`

const TextContent = styled.div<{ $textWidth: string }>`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: ${(p) => p.$textWidth};
  max-width: 100%;
  min-width: 0;
  flex-shrink: 1;
  text-align: left;
`

const Title = styled.div`
  font-size: 22px;
  font-weight: 600;
  line-height: 25px;
  color: #28364f;
  overflow-wrap: break-word;
`

const Description = styled.div`
  font-size: 14px;
  line-height: 25px;
  color: ${lightText};
  overflow-wrap: break-word;
`

const ActionsWrapper = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`
